/**
 * Form behavior rules — canonical schema.
 *
 * Created in 0.36.88 as the foundation of Phase 1: form logic
 * (visibility, required, value-setting) lives in a centralized
 * `rules[]` array on each form translation, alongside `structure`.
 * The two layers are independently editable: `structure` says what
 * fields exist and how they look; `rules` says how they behave.
 *
 * This file is the API contract. Three audiences read it:
 *   1. The runtime evaluator (Phase 1, next ship) — interprets rules
 *      against form values to produce visibility/required/value state.
 *   2. The visual rule editor (Phase 1, follow-up ship) — generates UI
 *      for building and editing rule objects.
 *   3. External callers (future BPM / automation) — write rule
 *      objects directly via the API.
 *
 * Hand-editing the rules JSON is also supported (no UI yet — that's
 * a follow-up ship). The shape is intentionally human-readable so
 * developers can author rules in a text editor.
 *
 * =================================================================
 * RULE SHAPE
 * =================================================================
 *
 *   {
 *     "id":       string,       // stable rule id (cross-system reference)
 *     "name":     string,       // human-readable label for the rule list
 *     "enabled":  boolean,      // toggle without deleting; default true
 *     "when":     Condition,    // when does the rule "fire"?
 *     "then":     Effect[],     // effects applied when `when` is true
 *     "else":     Effect[]?     // optional; applied when `when` is false
 *   }
 *
 * A form's logic is the array `rules[]` of these objects. Rules
 * evaluate independently in order; later rules can override earlier
 * effects on the same fields (last-write-wins per field per effect).
 *
 * =================================================================
 * CONDITION SHAPE
 * =================================================================
 *
 * A `when` block has two optional clauses, `all` and `any`. Either
 * may be omitted. At least one must be non-empty (a rule with no
 * conditions never fires).
 *
 *   {
 *     "all": [LeafCondition, ...]?,   // AND-joined — every leaf must match
 *     "any": [LeafCondition, ...]?    // OR-joined  — at least one must match
 *   }
 *
 * When both are present, BOTH clauses must be satisfied (AND between
 * the clauses). This lets you express patterns like:
 *
 *   "field A is X, AND (field B is Y OR field B is Z)"
 *
 *   { "all": [{ field: "A", op: "equals", value: "X" }],
 *     "any": [{ field: "B", op: "equals", value: "Y" },
 *             { field: "B", op: "equals", value: "Z" }] }
 *
 * (Note: that specific example is equivalent to using `op: "in"` for
 * field B. `any` is most useful when ORing across DIFFERENT fields.)
 *
 * LeafCondition:
 *
 *   { "field": string, "op": Operator, "value": any? }
 *
 * Operators:
 *
 *   - "equals"       — strict-equality compare
 *   - "not_equals"   — strict-inequality
 *   - "in"           — value is an array; field's value is in it
 *   - "not_in"       — value is an array; field's value is NOT in it
 *   - "is_set"       — field has any non-empty value
 *   - "is_empty"     — field has no value
 *
 * Examples:
 *
 *   { "field": "action_type", "op": "equals",  "value": "A2" }
 *   { "field": "region",      "op": "in",      "value": ["EU", "EEA"] }
 *   { "field": "region",      "op": "not_in",  "value": ["USA", "CAN"] }
 *   { "field": "notes",       "op": "is_empty" }
 *
 * For more complex logic (e.g. "(A OR B) AND (C OR D)"), define
 * MULTIPLE RULES — each rule evaluates independently and their
 * effects combine in order. The conditions of one rule can't directly
 * "see" another rule's results; if you need that, structure the
 * effects so a later rule keys off a field set by an earlier rule's
 * `set_value`.
 *
 * =================================================================
 * EFFECT SHAPE
 * =================================================================
 *
 *   { "effect": "show",          "fields": string[] }
 *   { "effect": "hide",          "fields": string[] }
 *   { "effect": "set_required",  "fields": string[], "required": boolean }
 *   { "effect": "clear_value",   "fields": string[] }
 *   { "effect": "set_value",     "fields": string[], "value": any }
 *   { "effect": "lock",          "fields": string[] }
 *
 * =================================================================
 * ELSE BRANCH (optional)
 * =================================================================
 *
 *   {
 *     "else":      Effect[],     // applied when `when` is false
 *     "else_when": Condition?    // optional gate (since 0.36.109)
 *   }
 *
 * - `else` (when present) lists effects to apply when the `when`
 *   doesn't match.
 *
 * - `else_when` (when present) is an extra condition block (same
 *   shape as `when` — `all` and/or `any` clauses). The else branch
 *   fires only when (`when` fails) AND (`else_when` matches). When
 *   absent or empty, else fires whenever `when` fails (legacy
 *   behavior).
 *
 * - With `else_when`, a rule can have THREE outcomes:
 *     1. `when` matches            → THEN branch fires
 *     2. `when` fails, `else_when` matches  → ELSE branch fires
 *     3. `when` fails, `else_when` fails    → NEITHER branch fires
 *
 * =================================================================
 * SUBMISSION SEMANTICS
 * =================================================================
 *
 *   - Hidden fields are INCLUDED in the submission payload, tagged
 *     with `__hidden: true`. Reason: BPM / automation can see what
 *     a rule-set value WOULD have been even if the user didn't see
 *     the field.
 *
 *   - `set_value` fields submit the rule-set value even when hidden.
 *
 *   - Required validation is SKIPPED for hidden fields, regardless
 *     of whether static `required` or rule's `set_required` said
 *     true. "Required" means "required when visible".
 *
 *   - Rule evaluation reads the actual stored value of each field
 *     (not a visibility-filtered view). So a rule that depends on
 *     field X sees X's value whether X is currently visible or not.
 *     Order-independent.
 *
 * =================================================================
 * FUTURE EXTENSIONS (NOT YET SHIPPED)
 * =================================================================
 *
 *   - Nested boolean trees (currently `all` and `any` are flat siblings;
 *     no nested grouping like `(A AND B) OR (C AND D)`)
 *   - More operators: `contains` / `greater_than` / `less_than` / `range`
 *   - JS expression escape hatch
 *   - Server-side evaluation for hardening
 *   - BPM-driven rule sets from external systems
 *   - Switch / case shorthand for N-branch rules over a single field
 */

/**
 * @typedef {Object} LeafCondition
 * @property {string} field
 * @property {'equals'|'not_equals'|'in'|'not_in'|'is_set'|'is_empty'} op
 * @property {*} [value]
 */

/**
 * @typedef {Object} Condition
 * @property {LeafCondition[]} [all]
 * @property {LeafCondition[]} [any]
 */

/**
 * @typedef {Object} Effect
 * @property {'show'|'hide'|'set_required'|'clear_value'|'set_value'|'lock'} effect
 * @property {string[]} fields
 * @property {boolean} [required]
 * @property {*} [value]
 */

/**
 * @typedef {Object} Rule
 * @property {string} id
 * @property {string} name
 * @property {boolean} enabled
 * @property {Condition} when
 * @property {Effect[]} then
 * @property {Effect[]} [else]
 * @property {Condition} [else_when]
 */

export const OPERATORS = [
  'equals',
  'not_equals',
  // 0.47.73 - Number comparisons.
  'less_than',
  'more_than',
  'at_least',
  'at_most',
  'in',
  'not_in',
  'is_set',
  'is_empty',
  // 0.37.28 — Per-field "modified from initial" check. True when
  // the field's current value differs from its default value (or
  // empty when it had no default). Flips back to false if the
  // user types the original value again. See evaluateLeaf in
  // evaluate.js and the changedFields set in the runtime.
  'is_changed',
  'is_unchanged',
  // 0.37.32 — Button-click edge condition. True only for the
  // render immediately following a click on the button whose
  // element_id matches `leaf.field`. Flips false on the next
  // render. Lets rules react to clicks without stuffing all the
  // click logic into the button's action_value textarea.
  'just_clicked',
  // 0.41.16 - API call state, so a form can react to a call it
  // started. `leaf.field` names the CONNECTION.
  //
  // Without these a four-second call is invisible: nothing can
  // disable the button while it runs, and a failure looks exactly
  // like nothing having happened.
  //
  // `api_failed` is deliberately coarse - the class of failure, not
  // the far end's message. A raw error can name internal hosts and
  // paths, and the citizen-facing wording belongs in an Alert the
  // admin writes and translates.
  'api_pending',
  'api_ok',
  'api_failed',
  // 0.43.67 - The API answered, and one field of its answer is a given
  // value: { field: 'haiku.sum_check', op: 'api_answer_is',
  // path: 'answer.verdict', value: 'review' }.
  'api_answer_is',
  // 0.48.29 - The result of "create account" (field: "account").
  'account_created',
  'account_pending',
  'account_running',
  'account_failed',
  // 0.48.38
  'account_code_sent',
  // 0.48.52 - The result of "update account" (field: "account_update").
  'account_updated',
  'account_update_running',
  'account_update_code_sent',
  'account_update_failed',
  // 0.37.42 — Form-level operator: true when every input in the
  // form that is currently required AND currently visible has a
  // non-empty value. Ignores hidden inputs and non-required
  // inputs. `leaf.field` is unused (operator is form-scoped);
  // the editor hides the field picker when this op is selected.
  // Rule order matters: this operator sees hide / set_required
  // effects from rules earlier in the list, not later ones.
  'all_required_filled',
  // 0.37.47 — Negation of all_required_filled. Also form-scoped.
  // Same semantics inverted: true when at least ONE required
  // visible input is empty. Not just !all_required_filled at the
  // rule authoring level — useful as its own operator so admin
  // can attach `then` effects to the "form incomplete" branch
  // (e.g. show error message on required fields) without having
  // to write an else branch on a positive rule.
  'not_all_required_filled',
  // 0.48.30 - Offered by the visual editor, missing here (the JSON view
  // refused them).
  'always',
  'never',
  'page_loads',
];

// Kept as an alias for the older name used by some imports.
export const PHASE_1_OPERATORS = OPERATORS;

export const EFFECTS = [
  'show',
  'hide',
  'set_required',
  'clear_value',
  'set_value',
  'lock',
  // 0.36.711 — Element-render overrides. `set_label` overrides the
  // element's label at render time; `set_action_value` overrides a
  // button's action_value (used e.g. to swap select_all ↔ clear_all
  // on the same button based on table state). Both are reactive —
  // when the rule's condition stops matching, the element reverts
  // to its base value.
  'set_label',
  // 0.37.76 — set_text overrides an element's `text` at render
  // time (title, hint, markdown). Companion to set_label, which
  // overrides an input's `label`. Reactive — same shape as
  // set_label. Renderer reads via state.texts.get(fieldName) and
  // falls back to element.text when no override is set.
  'set_text',
  'set_action_value',
  // 0.37.85 — Manual-table mutation effects (Shape A of the
  // "control what a table shows at runtime" ask). All three
  // target a table element by its element_id and land in
  // ruleState.tableOverlays; renderer merges the overlay onto
  // e.rows at render time. Sticky-on-click via the same
  // pipeline set_text uses (see 0.37.79) so click-triggered
  // rules don't flash-and-revert.
  //
  //   set_cell:    override a single (row, column) cell value.
  //                Row/column are 1-based to match admin's
  //                mental model (Rule 1, Row 1, Column 1).
  //                Value accepts literals, {placeholder}, or
  //                =expressions the same way set_value does.
  //   add_row:     append a row to the table's effective row
  //                list. Args carry `values` as an array
  //                aligned to columns.
  //   remove_row:  remove the row at the given 1-based index
  //                from the effective row list.
  'set_cell',
  'add_row',
  'remove_row',
  // 0.36.282 — Dropdown / multiselect effects.
  'set_sort_order',
  'add_option',
  // 0.49.82 - Leave values out of a dropdown's list (see evaluate.js).
  'remove_option',
  // 0.37.47 — Error-message effects. `set_error_message` writes
  // the error text (empty text = clear). It auto-flips the
  // show flag so the common case is one effect. `show_error_message`
  // and `hide_error_message` toggle the visibility flag alone —
  // useful for triggering errors set by other rules, or reverting
  // a rule-set error without disturbing its text.
  'set_error_message',
  'show_error_message',
  'hide_error_message',
  // 0.37.48 — Reset a field's "changed" baseline to its current
  // value. `is_changed` / `is_unchanged` diff current values
  // against an initial-values baseline; this effect updates that
  // baseline for the named fields, so a field that was changed
  // starts reading as unchanged. Edge-triggered (like set_value)
  // — snapshots values at the moment the rule newly fires;
  // stable until another mark_unchanged fires for the same
  // field or the citizen edits it again.
  'mark_unchanged',
  // 0.48.96 - Back to the last unchanged value (Discard).
  'restore_value',
  // 0.41.16 - Call a registered API connection. Unlike every other
  // effect this targets no element: its args are a connection and a
  // list of input bindings. See EFFECT_ARGS below and the handling in
  // evaluate.js, which runs BEFORE the field-scoped machinery.
  'call_api',
  // 0.41.25 - Put a value from an API response into a field.
  //
  // Unlike call_api this DOES target elements, so it goes through the
  // normal field machinery. Its extra args are the connection whose
  // response to read and the path within it.
  'set_from_api',
  // 0.48.29 - Create an account on the server, optionally checked with
  // an API (services/form_signup.py). Targets no element.
  'create_account',
  // 0.48.38 - Its second step when it confirms the email with a code.
  'confirm_account_code',
  'resend_account_code',
  // 0.48.52 - The signed-in user's email and names, from an API or page
  // fields (services/account_update.py). Targets no element.
  'update_account',
  // 0.48.63 - Open / close a Modal (its Element ID in fields).
  'open_modal',
  'close_modal',
  // 0.48.64 - Write values into a table's clicked row (Edit link).
  'set_row',
  // 0.48.89 - A server copy of an API answer (Settings, Server copies):
  // fill it, change paths in it, put it back to the API's answer.
  'server_copy_fill',
  'server_copy_set',
  'server_copy_reset',
  // 0.48.30 - Effects the visual editor has offered for a long time but
  // this list (which the JSON view checks against) never had, so a rule
  // using one could not be saved from the JSON view.
  'set_variant',
  'show_required_errors',
  'scroll_to',
  'flow_next',
  'flow_previous',
  'flow_first',
  'flow_restart',
  'set_flow_error',
];

export const PHASE_1_EFFECTS = EFFECTS;

export const OPERATOR_IS_BINARY = {
  equals: true,
  not_equals: true,
  in: true,
  not_in: true,
  is_set: false,
  is_empty: false,
  // 0.37.28 — Value-free like is_set / is_empty.
  is_changed: false,
  is_unchanged: false,
  // 0.37.32 — Also value-free; the operand is the button element's
  // element_id, no comparison value.
  just_clicked: false,
  // 0.37.42 — Value-free AND field-free (form-level).
  all_required_filled: false,
  // 0.37.47 — Same shape as its positive twin.
  not_all_required_filled: false,
};

export const EFFECT_ARGS = {
  show:             [],
  hide:             [],
  set_required:     ['required'],
  clear_value:      [],
  set_value:        ['value'],
  lock:             [],
  set_label:        ['label'],
  // 0.37.76 — Companion to set_label; for title / hint / markdown
  // elements whose displayed content is `text`, not `label`.
  set_text:         ['text'],
  // 0.41.16 - Call a registered API connection.
  //
  // The engine cannot PERFORM this: evaluateRules runs inside a
  // useMemo and must stay pure and synchronous, while a call takes
  // seconds. So the effect REQUESTS a call; the renderer dispatches
  // it and feeds the result back in, the way library data already
  // flows.
  //
  // Restricted to a click trigger for now. An expensive third-party
  // call fired by a change event would run once per keystroke.
  call_api:         ['connection', 'inputs'],
  set_from_api:     ['connection', 'path', 'separator'],
  // 0.48.29 - email_field, password_field, connection (optional),
  // inputs (as call_api), match [{path, field}], fill {first_name,
  // last_name, ssn: answer paths}, approval 'wait' | 'active'.
  create_account:   ['email_field', 'password_field', 'confirm_field', 'connection', 'inputs', 'match', 'fill', 'approval', 'show_expected', 'verify_email'],
  confirm_account_code: ['code_field'],
  resend_account_code: [],
  // 0.48.52 - connection (optional), inputs, ssn_path (the API's social
  // card number, which must be the account's), set {email, first_name,
  // last_name: {source: 'api', path} | {source: 'field', field}},
  // verify_email.
  update_account:   ['connection', 'inputs', 'ssn_path', 'set', 'verify_email'],
  open_modal:       [],
  close_modal:      [],
  // 0.48.64 - values: [{ column, value }]; column is the row's key
  // (c1, c2 for a manual table; a library column; an API path).
  set_row:          ['values'],
  // 0.48.89 - values: [{ path, value }] for change; fill and reset take
  // only the copy's name (fields).
  server_copy_fill:  [],
  server_copy_set:   ['values'],
  server_copy_reset: [],
  set_action_value: ['value'],
  // 0.37.85 — Table-mutation effect args. set_cell targets one
  // cell by 1-based (row, column); admin gives a value that
  // renderer processes with the same interpolation +
  // expression-eval treatment as static cell text (Shape B).
  // add_row's `values` is an array of cell values aligned to
  // the table's columns; short arrays pad with empty strings
  // at render time. remove_row's `row` is 1-based; out-of-
  // range no-ops silently.
  set_cell:         ['row', 'column', 'value'],
  add_row:          ['values'],
  remove_row:       ['row'],
  set_sort_order:   ['order'],
  add_option:       ['value', 'label'],
  remove_option:    ['value'],
  // 0.37.47 — Error-message effects. set_error_message carries
  // the `message` string; show/hide have no additional args.
  set_error_message:  ['message'],
  show_error_message: [],
  hide_error_message: [],
  // 0.37.48 — No args — just the target fields via effect.fields.
  mark_unchanged:     [],
  restore_value:      [],
};

export function blankRule(id) {
  return {
    id: id || `rule-${Date.now()}`,
    name: '',
    enabled: true,
    when: { all: [], any: [] },
    then: [],
    else: [],
    // else_when stays out of the blank template — it's optional and
    // most rules don't need it. Editor will add the keys if the
    // admin populates the section.
  };
}
